import React, { useState } from 'react';
import {
  TrendingUp, TrendingDown, DollarSign, PieChart, BarChart3,
  Calendar, Download, Filter, ArrowUpRight, ArrowDownRight,
  Wallet, Receipt, CreditCard, Activity
} from 'lucide-react';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const DashboardTab = () => {
  const [dateRange, setDateRange] = useState('month');

  // P&L Data
  const plData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Revenue',
        data: [1200000, 1350000, 1450000, 1320000, 1580000, 1650000, 1720000, 1680000, 1750000, 1820000, 1900000, 1950000],
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Expenses',
        data: [950000, 1020000, 1100000, 1050000, 1180000, 1250000, 1320000, 1280000, 1350000, 1420000, 1480000, 1520000],
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Net Income',
        data: [250000, 330000, 350000, 270000, 400000, 400000, 400000, 400000, 400000, 400000, 420000, 430000],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
        borderDash: [5, 5]
      }
    ]
  };

  // Cash Flow Data
  const cashFlowData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Cash Inflow',
        data: [1200000, 1350000, 1450000, 1320000, 1580000, 1650000],
        backgroundColor: 'rgba(34, 197, 94, 0.8)'
      },
      {
        label: 'Cash Outflow',
        data: [950000, 1020000, 1100000, 1050000, 1180000, 1250000],
        backgroundColor: 'rgba(239, 68, 68, 0.8)'
      }
    ]
  };

  // Account Distribution
  const accountDistributionData = {
    labels: ['Assets', 'Liabilities', 'Equity', 'Revenue', 'Expenses'],
    datasets: [
      {
        data: [45, 30, 15, 7, 3],
        backgroundColor: [
          'rgba(34, 197, 94, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(59, 130, 246, 0.8)',
          'rgba(168, 85, 247, 0.8)',
          'rgba(245, 158, 11, 0.8)'
        ]
      }
    ]
  };

  const plOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Profit & Loss Statement'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return 'SAR ' + (value / 1000).toFixed(0) + 'K';
          }
        }
      }
    }
  };

  const cashFlowOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  const keyMetrics = [
    {
      label: 'Current Ratio',
      value: '1.51',
      change: '+0.05',
      trend: 'up',
      description: 'Current Assets / Current Liabilities'
    },
    {
      label: 'Quick Ratio',
      value: '1.23',
      change: '+0.03',
      trend: 'up',
      description: '(Current Assets - Inventory) / Current Liabilities'
    },
    {
      label: 'Debt-to-Equity',
      value: '0.66',
      change: '-0.02',
      trend: 'down',
      description: 'Total Debt / Total Equity'
    },
    {
      label: 'Working Capital',
      value: 'SAR 4,220,000',
      change: '+5.2%',
      trend: 'up',
      description: 'Current Assets - Current Liabilities'
    }
  ];

  const agedReceivables = [
    { period: 'Current', amount: 1250000, percentage: 58 },
    { period: '1-30 days', amount: 450000, percentage: 21 },
    { period: '31-60 days', amount: 280000, percentage: 13 },
    { period: '61-90 days', amount: 115000, percentage: 5 },
    { period: 'Over 90 days', amount: 50000, percentage: 3 }
  ];

  const agedPayables = [
    { period: 'Current', amount: 890000, percentage: 47 },
    { period: '1-30 days', amount: 520000, percentage: 28 },
    { period: '31-60 days', amount: 320000, percentage: 17 },
    { period: '61-90 days', amount: 120000, percentage: 6 },
    { period: 'Over 90 days', amount: 40000, percentage: 2 }
  ];

  return (
    <div className="space-y-6">
      {/* Date Range Filter */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium bg-white"
          >
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
            <option value="year">This Year</option>
          </select>
          <button className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-2">
            <Filter className="h-4 w-4" />
            More Filters
          </button>
        </div>
        <button className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2">
          <Download className="h-4 w-4" />
          Export Report
        </button>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {keyMetrics.map((metric, index) => (
          <div key={index} className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-5 border border-gray-200 shadow-sm">
            <p className="text-sm font-medium text-gray-600 mb-1">{metric.label}</p>
            <p className="text-2xl font-bold text-gray-900 mb-1">{metric.value}</p>
            <div className="flex items-center gap-2">
              {metric.trend === 'up' ? (
                <ArrowUpRight className="h-4 w-4 text-green-600" />
              ) : (
                <ArrowDownRight className="h-4 w-4 text-red-600" />
              )}
              <span className={`text-sm font-medium ${metric.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                {metric.change}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-2">{metric.description}</p>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* P&L Chart */}
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <div className="h-80">
            <Line data={plData} options={plOptions} />
          </div>
        </div>

        {/* Cash Flow Chart */}
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <div className="h-80">
            <Bar data={cashFlowData} options={cashFlowOptions} />
          </div>
        </div>
      </div>

      {/* Account Distribution and Aged Reports */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Account Distribution */}
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Distribution</h3>
          <div className="h-64">
            <Doughnut data={accountDistributionData} />
          </div>
        </div>

        {/* Aged Receivables */}
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Aged Receivables</h3>
            <Receipt className="h-5 w-5 text-green-600" />
          </div>
          <div className="space-y-4">
            {agedReceivables.map((item, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">{item.period}</span>
                  <span className="text-sm font-semibold text-gray-900">
                    SAR {(item.amount / 1000).toFixed(0)}K
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full transition-all"
                    style={{ width: `${item.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-900">Total</span>
              <span className="text-lg font-bold text-gray-900">SAR 2,145,000</span>
            </div>
          </div>
        </div>

        {/* Aged Payables */}
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Aged Payables</h3>
            <CreditCard className="h-5 w-5 text-red-600" />
          </div>
          <div className="space-y-4">
            {agedPayables.map((item, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">{item.period}</span>
                  <span className="text-sm font-semibold text-gray-900">
                    SAR {(item.amount / 1000).toFixed(0)}K
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-red-600 h-2 rounded-full transition-all"
                    style={{ width: `${item.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-900">Total</span>
              <span className="text-lg font-bold text-gray-900">SAR 1,890,000</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Recent Financial Activity</h3>
          <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">View All</button>
        </div>
        <div className="space-y-3">
          {[
            { type: 'Journal Entry', description: 'Monthly depreciation entry', amount: 'SAR 45,000', date: '2 hours ago', status: 'Posted' },
            { type: 'Invoice', description: 'Invoice #INV-2024-001', amount: 'SAR 125,000', date: '5 hours ago', status: 'Paid' },
            { type: 'Payment', description: 'Vendor payment - ABC Supplies', amount: 'SAR 89,500', date: '1 day ago', status: 'Cleared' },
            { type: 'Reconciliation', description: 'Bank reconciliation completed', amount: 'SAR 0', date: '2 days ago', status: 'Matched' },
            { type: 'Asset', description: 'New equipment capitalized', amount: 'SAR 250,000', date: '3 days ago', status: 'Active' }
          ].map((activity, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Activity className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{activity.type}</p>
                  <p className="text-xs text-gray-600">{activity.description}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-900">{activity.amount}</p>
                <p className="text-xs text-gray-500">{activity.date}</p>
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded ${
                activity.status === 'Posted' || activity.status === 'Paid' || activity.status === 'Cleared' || activity.status === 'Matched' || activity.status === 'Active'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-yellow-100 text-yellow-700'
              }`}>
                {activity.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DashboardTab;
